import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/synapse/Icon';
import { StatusDot } from '@/components/synapse/StatusDot';
import { SummaryCard, SummaryCardRow } from '@/components/synapse/SummaryCard';
import { useCan } from '@/hooks/usePermissions';
import { translateApiError } from '@/lib/apiError';
import { orderStatusTone, translateOrderStatus } from '@/lib/labTests';
import { usePatientLabOrders, useLabReportUrl } from './useLab';
import type { LabOrderSummary, LabTest } from '@/types/lab';

interface PatientLabsCardProps {
  patientId: string;
  /**
   * The registry's demographics-only view. Hides the "new order" action; the
   * lab history itself still shows, because knowing a sample was taken is not
   * clinical detail.
   */
  readOnly?: boolean;
  /** How many orders to show before pointing at the worklist. */
  limit?: number;
}

const TONE_TO_DOT = {
  success: 'ok',
  info: 'warn',
  danger: 'danger',
  neutral: 'neutral',
  muted: 'neutral',
} as const;

/**
 * This patient's lab history, newest first.
 *
 * Rendered inside the patient record rather than only on the lab desk because
 * that is where the question is actually asked — a clinician reading a chart
 * wants last month's FBC without going to find it.
 *
 * Three permissions are in play and they are deliberately separate. `lab.read`
 * decides whether the card renders at all; `lab.orders.create` decides whether
 * the new-order action appears; `lab.results.read` decides whether a report can
 * be OPENED. A member with the first but not the last sees that a report exists
 * and cannot read it — which is the intended shape, so that reception can chase
 * an outstanding result without reading the diagnosis in it.
 */
export function PatientLabsCard({ patientId, readOnly, limit = 5 }: PatientLabsCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();

  const canRead = can('lab.read');
  const orders = usePatientLabOrders(patientId, { limit, enabled: canRead });

  // Rendering nothing is correct here: a member without `lab.read` is not being
  // told a lab section exists and is being withheld.
  if (!canRead) return null;

  const rows = orders.data ?? [];

  return (
    <SummaryCard
      titleKey="lab.patient.title"
      icon="flask"
      action={
        !readOnly && can('lab.orders.create') ? (
          <button
            type="button"
            onClick={() => navigate(`/lab/new?patientId=${patientId}`)}
            className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink"
          >
            <Icon name="plus" size={12} />
            {t('lab.newOrder')}
          </button>
        ) : null
      }
    >
      {orders.isPending && (
        <SummaryCardRow first>
          <div className="h-4 w-40 animate-pulse rounded bg-paper-2" />
        </SummaryCardRow>
      )}

      {orders.isError && (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-destructive">{t('lab.patient.loadFailed')}</span>
        </SummaryCardRow>
      )}

      {!orders.isPending && !orders.isError && rows.length === 0 && (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-muted">{t('lab.patient.empty')}</span>
        </SummaryCardRow>
      )}

      {rows.map((order, i) => (
        <SummaryCardRow key={order.id} first={i === 0}>
          <OrderRow order={order} onOpen={() => navigate(`/lab/${order.id}`)} />
        </SummaryCardRow>
      ))}

      {rows.length >= limit && (
        <SummaryCardRow>
          <button
            type="button"
            // Carries the patient through. Without it this lands on every
            // report in the clinic and the reader has to retype the name they
            // were already looking at.
            onClick={() => navigate(`/lab/reports?patientId=${patientId}`)}
            className="text-[12.5px] text-muted underline hover:text-ink"
          >
            {t('lab.patient.seeAll')}
          </button>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );
}

function OrderRow({ order, onOpen }: { order: LabOrderSummary; onOpen: () => void }) {
  const { t } = useTranslation();
  const live = order.tests.filter((test) => test.status !== 'cancelled');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <button
          type="button"
          onClick={onOpen}
          aria-label={t('lab.openOrder', { accession: order.accessionNumber })}
          className="font-mono text-[12.5px] tabular-nums text-ink underline-offset-2 hover:underline"
        >
          {order.accessionNumber}
        </button>
        <span className="text-[12px] text-muted">
          {order.createdAt.toLocaleDateString()}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[12px] text-muted">
          <StatusDot tone={TONE_TO_DOT[orderStatusTone(order.status)]} />
          {translateOrderStatus(t, order.status)}
        </span>
        {order.priority === 'urgent' && (
          <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[10.5px] font-medium text-accent-foreground">
            {t('lab.priority.urgent')}
          </span>
        )}
      </div>

      <ul className="flex flex-col gap-1">
        {live.map((test) => (
          <li key={test.id}>
            <TestLine test={test} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TestLine({ test }: { test: LabTest }) {
  const { t } = useTranslation();
  const can = useCan();
  const mint = useLabReportUrl();
  const [error, setError] = useState<string | null>(null);

  const hasReport = test.resultDocumentId !== null;

  async function open() {
    if (!test.resultDocumentId) return;
    setError(null);
    try {
      // Minted on click, never ahead of time: the server writes a
      // `phi_access_log` row when it mints, so that row must mean a person
      // actually opened the report.
      const { url } = await mint.mutateAsync(test.resultDocumentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.detail.openFailed') }));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[12.5px] text-ink-2">{test.name}</span>

      {!hasReport && (
        <span className="text-[11.5px] text-muted-2">{t('lab.detail.awaitingReport')}</span>
      )}

      {hasReport && can('lab.results.read') && (
        <button
          type="button"
          onClick={() => void open()}
          disabled={mint.isPending}
          aria-label={t('lab.reportFor', { test: test.name })}
          className="inline-flex items-center gap-1 text-[11.5px] text-muted underline underline-offset-2 hover:text-ink disabled:opacity-50"
        >
          <Icon name="arrow_up_right" size={11} />
          {t('lab.openReport')}
        </button>
      )}

      {hasReport && !can('lab.results.read') && (
        <span className="text-[11.5px] text-muted-2">{t('lab.patient.reportOnFile')}</span>
      )}

      {error && <span className="text-[11.5px] text-destructive">{error}</span>}
    </div>
  );
}
